import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Ship, Plane, Truck, Boxes, FileCheck, Warehouse,
  Globe, Shield, Users, Cpu, HeartHandshake, Clock, CheckCircle2,
  Star, Network
} from 'lucide-react';
import Hero from '../components/Hero';
import './Home.css';

const Home = () => {
  const services = [
    {
      icon: Ship,
      title: 'Sea Freight',
      description: 'Reliable and cost-effective ocean freight solutions including FCL, LCL, and cargo consolidation across major ports worldwide.',
      link: '/services/sea-freight',
    },
    {
      icon: Plane,
      title: 'Air Freight',
      description: 'Customer-specific air freight solutions with fast transit, secure handling, and continuous communication.',
      link: '/services/air-freight',
    },
    {
      icon: Truck,
      title: 'Land Freight',
      description: 'Reliable land freight solutions for domestic and cross-border transportation with complete visibility.',
      link: '/services/land-freight',
    },
    {
      icon: Boxes,
      title: 'Project Logistics',
      description: 'End-to-end project logistics for oversized, heavy-lift, and high-value cargo across industries.',
      link: '/services/project-logistics',
    },
    {
      icon: FileCheck,
      title: 'Customs Clearance',
      description: 'Efficient and compliant customs clearance ensuring smooth movement of cargo across borders.',
      link: '/services/customs-clearance',
    },
    {
      icon: Warehouse,
      title: 'Warehousing & Value-Added Services',
      description: 'Secure, scalable warehousing solutions to optimize inventory and streamline supply chains.',
      link: '/services/warehousing',
    },
  ];

  const strengths = [
    { icon: CheckCircle2, title: '100% Customer-Focused Logistics Solutions' },
    { icon: Users, title: 'Professionally Managed by an Experienced Leadership Team' },
    { icon: Cpu, title: 'State-of-the-Art Logistics Technology' },
    { icon: HeartHandshake, title: 'Advanced Customer Relationship Management (CRM) System' },
    { icon: Star, title: 'Highly Motivated and Dedicated Logistics Professionals' },
    { icon: Network, title: 'Strong Global Network of Partners and Agents' },
    { icon: Shield, title: 'Reliable, Transparent, and Cost-Effective Services' },
    { icon: Clock, title: 'Commitment to On-Time Delivery and Customer Satisfaction' },
  ];

  const industries = [
    'Automotive & Auto Components',
    'Engineering & Industrial Equipment',
    'Pharmaceuticals & Healthcare',
    'Chemicals & Specialty Products',
    'Electronics & Technology',
    'Textiles & Garments',
    'FMCG & Consumer Goods',
    'Retail & E-Commerce',
    'Food & Beverage',
    'Renewable Energy',
    'Project & Heavy Engineering',
    'Oil & Gas',
  ];

  return (
    <div className="home">
      {/* Hero */}
      <Hero
        label="Logistics Redefined"
        title="Reliable. Efficient. Cost-Effective."
        description="Denver Logistics Private Limited is an emerging and trusted logistics and freight forwarding company committed to delivering reliable, efficient, and cost-effective supply chain solutions."
        primaryBtn={{ text: 'Get a Quote', to: '/quote' }}
        secondaryBtn={{ text: 'Explore Our Services', to: '/services/sea-freight' }}
        size="large"
        image="/img/hero-ship.jpg"
      />

      {/* Who We Are */}
      <section className="section home-about">
        <div className="container">
          <div className="home-about__grid">
            <div className="home-about__content">
              <span className="section-label">Who We Are</span>
              <h2 className="section-title">
                Delivering Reliable Logistics.<br />
                Building Stronger Partnerships.
              </h2>
              <p>
                Denver Logistics Private Limited is one of the emerging and trusted logistics
                and freight forwarding companies, committed to delivering reliable, efficient,
                and cost-effective supply chain solutions.
              </p>
              <p>
                We provide comprehensive multimodal transportation services to customers across
                diverse industries through our extensive network of global partners, agents,
                and strategic alliances.
              </p>
              <Link to="/about" className="btn btn-outline">
                More About Us <ArrowRight size={16} />
              </Link>
            </div>
            <div className="home-about__visual">
              <div className="home-about__stats">
                <div className="home-about__stat">
                  <Globe size={28} className="home-about__stat-icon" />
                  <h3>Global Network</h3>
                  <p>Extensive network of partners and agents across the globe</p>
                </div>
                <div className="home-about__stat">
                  <Ship size={28} className="home-about__stat-icon" />
                  <h3>Multimodal Solutions</h3>
                  <p>Air, sea, land, and integrated logistics services</p>
                </div>
                <div className="home-about__stat">
                  <Shield size={28} className="home-about__stat-icon" />
                  <h3>End-to-End</h3>
                  <p>From origin to destination, we manage every detail</p>
                </div>
                <div className="home-about__stat">
                  <CheckCircle2 size={28} className="home-about__stat-icon" />
                  <h3>Customer First</h3>
                  <p>Your success is our priority, always</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section home-services" id="services">
        <div className="container">
          <div className="text-center">
            <span className="section-label">Our Services</span>
            <h2 className="section-title">Comprehensive Logistics Solutions</h2>
            <p className="section-subtitle">
              Our integrated logistics solutions cover every aspect of your supply chain,
              ensuring every shipment is handled with precision and professionalism.
            </p>
          </div>
          <div className="home-services__grid">
            {services.map((service, index) => (
              <Link to={service.link} key={index} className="service-tile">
                <div className="service-tile__icon-wrap">
                  <service.icon size={28} />
                </div>
                <h3 className="service-tile__title">{service.title}</h3>
                <p className="service-tile__desc">{service.description}</p>
                <span className="service-tile__link">
                  Explore <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Denver Logistics */}
      <section className="section home-strengths">
        <div className="container">
          <div className="text-center">
            <span className="section-label">Why Denver Logistics</span>
            <h2 className="section-title">Why Choose Us?</h2>
            <p className="section-subtitle">
              Our commitment to excellence, technology-driven approach, and global network
              set us apart as your trusted logistics partner.
            </p>
          </div>
          <div className="home-strengths__grid">
            {strengths.map((item, index) => (
              <div key={index} className="strength-card">
                <div className="strength-card__icon">
                  <item.icon size={24} />
                </div>
                <p className="strength-card__title">{item.title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Network */}
      <section className="section home-network">
        <div className="container">
          <div className="home-network__inner">
            <div className="home-network__content">
              <span className="section-label">Global Network</span>
              <h2 className="section-title" style={{ color: 'var(--white)' }}>
                Connecting Businesses Across Global Trade Lanes
              </h2>
              <p style={{ color: 'var(--gray-300)' }}>
                Our extensive network of global partners, agents, and strategic alliances enables us
                to provide comprehensive multimodal transportation and supply-chain solutions across
                international markets.
              </p>
              <Link to="/global-network" className="btn btn-white" style={{ marginTop: '24px' }}>
                Our Network <ArrowRight size={16} />
              </Link>
            </div>
            <div className="home-network__map">
              <div className="home-network__map-visual">
                <Globe size={120} strokeWidth={0.5} />
                <div className="home-network__dots">
                  <span className="home-network__dot" style={{ top: '30%', left: '20%' }} />
                  <span className="home-network__dot" style={{ top: '40%', left: '50%' }} />
                  <span className="home-network__dot" style={{ top: '35%', left: '70%' }} />
                  <span className="home-network__dot" style={{ top: '60%', left: '60%' }} />
                  <span className="home-network__dot" style={{ top: '45%', left: '80%' }} />
                  <span className="home-network__dot home-network__dot--active" style={{ top: '50%', left: '65%' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section home-industries">
        <div className="container">
          <div className="text-center">
            <span className="section-label">Industries We Serve</span>
            <h2 className="section-title">Supporting Diverse Industries</h2>
            <p className="section-subtitle">
              Denver Logistics Private Limited provides customized logistics and freight
              forwarding solutions across a wide range of industries.
            </p>
          </div>
          <div className="home-industries__grid">
            {industries.map((industry, index) => (
              <div key={index} className="industry-chip">
                {industry}
              </div>
            ))}
          </div>
          <div className="text-center" style={{ marginTop: '40px' }}>
            <Link to="/industries" className="btn btn-outline">
              View All Industries <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Conversion CTA */}
      <section className="section home-cta">
        <div className="container">
          <div className="home-cta__inner">
            <h2>Ready to Move Your Cargo?</h2>
            <p>
              Tell us about your requirement and our team will help identify the right logistics solution.
            </p>
            <Link to="/quote" className="btn btn-white btn-lg">
              Request a Quote <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
